import React from 'react';
import pageStyles from '../WarrantyRequest.module.scss';
import { ButtonColor } from '../../../../components/uikit/buttons/Button.tsx';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import clsx from 'clsx';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { useAdvancedForm } from '../../../../helpers/form/useAdvancedForm.ts';
import { BasketGetResponseDto } from '../../../../services/api/api-client.types.ts';
import { sendWarrantyRequest } from '../../../../services/api/api-client/WarrantyRequestBasketClient.ts';
import {
  LoadingType,
  WarrantyRequestPageCategory,
} from '../WarrantyRequestPage.tsx';
import { useWatch } from 'react-hook-form';
import { UseQueryResult } from '@tanstack/react-query';
import { Tip } from '../../../../components/uikit/Tip.tsx';
import { useAnalytics } from 'helpers/analytics.ts';

export type Form = {
  additionalEmail: string;
  reference: string;
};

type OwnProps = {
  closeSubmitModal: () => void;
  email: string;
  switchLoader: (value: LoadingType) => void;
  basketQuery: UseQueryResult<BasketGetResponseDto, unknown>;
  numberOfRows: number;
  handleChangeRequestNumber: (value: string) => void;
  submitButtonTestId?: string;
};

const SubmitModal = (props: OwnProps) => {
  const i18n = useScopedTranslation('Page.WarrantyPage');
  const { trackEvent } = useAnalytics();

  const {
    closeSubmitModal,
    email,
    handleChangeRequestNumber,
    numberOfRows,
    switchLoader,
    basketQuery,
    submitButtonTestId,
  } = props;

  const form = useAdvancedForm<Form>(
    async () => {
      trackEvent({
        category: WarrantyRequestPageCategory,
        action: 'Submit warranty request',
        name: `Total items in cart: ${numberOfRows}`,
      });
      try {
        if (numberOfRows !== 0) {
          switchLoader('submit');
          closeSubmitModal();
          const requestNumber = await sendWarrantyRequest(
            additionalEmail,
            reference,
          );
          handleChangeRequestNumber(requestNumber);
          void basketQuery.refetch();
        }
      } catch (e) {
        switchLoader(null);
      }
    },
    {
      defaultValues: {
        additionalEmail: '',
        reference: '',
      },
    },
  );

  const [additionalEmail, reference] = useWatch({
    control: form.control,
    name: ['additionalEmail', 'reference'],
  });

  return (
    <CustomModal
      isOpen={true}
      onSubmit={form.handleSubmitDefault}
      onCancel={closeSubmitModal}
      cancelButtonText={i18n.t('cancel')}
      submitButtonText={i18n.t('submit')}
      titleText={i18n.t('button_submit_warranty_request')}
      submitButtonType={ButtonColor.Primary}
      submitButtonTestId={submitButtonTestId}
    >
      <form>
        <div className={clsx(pageStyles.columnContainer, pageStyles.rowGap30)}>
          <div>
            <div
              className={clsx(
                pageStyles.rowContainer0,
                pageStyles.spaceBetween,
                pageStyles.marginBottom5,
              )}
            >
              <div
                className={clsx(
                  pageStyles.rowContainer0,
                  pageStyles.columnGap4,
                )}
              >
                <div className={pageStyles.label}>{i18n.t('reference')}</div>
                <Tip
                  className={pageStyles.tip}
                  tooltipText={i18n.t('reference_tooltip')}
                />
                <div className={pageStyles.disabled}>{i18n.t('optional')}</div>
              </div>
              <div className={pageStyles.disabled}>{reference.length}/20</div>
            </div>
            {/*abas limitation*/}
            <Input
              {...form.register('reference')}
              fullWidth={true}
              maxLength={20}
            />
          </div>
          <div
            className={clsx(pageStyles.columnContainer, pageStyles.rowGap15)}
          >
            <div className={pageStyles.label}>{i18n.t('recipient_email')}</div>
            <div>{email}</div>
          </div>

          <div>
            <div
              className={clsx(
                pageStyles.rowContainer0,
                pageStyles.spaceBetween,
                pageStyles.marginBottom5,
              )}
            >
              <div
                className={clsx(
                  pageStyles.rowContainer0,
                  pageStyles.columnGap4,
                )}
              >
                <div className={pageStyles.label}>
                  {i18n.t('additional_email')}
                </div>
                <Tip
                  className={pageStyles.tip}
                  tooltipText={i18n.t('email_tooltip')}
                />
                <div className={pageStyles.disabled}>{i18n.t('optional')}</div>
              </div>
            </div>
            {/*abas limitation*/}
            <Input
              {...form.register('additionalEmail')}
              fullWidth={true}
              maxLength={210}
            />
          </div>
        </div>
      </form>
    </CustomModal>
  );
};

export default SubmitModal;
